import { Component, ViewChild, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { finalize, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AngularSplitModule } from 'angular-split';

import { ValidationService } from '@app/core/services/validation.service';
import { ExecuteSqlDetail } from '@app/shared/CSDAModels/ExecuteSqlDetail';
import { ExecuteSqlResult } from '@app/shared/CSDAModels/ExecuteSqlResult';
import { environment } from '@env';
import { Language } from '@app/core/enums/Language';
import { SharedModule } from '@app/shared/shared.module';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { MatDialog } from '@angular/material/dialog';
import { CopyOrderDialogComponent } from "../Utilities/copy-order-dialog.component";
import { FloatingTooltipDirective } from "@app/shared/components/floating-tooltip/floating-tooltip.directive";
import { FloatingTooltipService } from "@app/shared/components/FloatingTooltipService.service";
import { ActiveOrderCCFormComponent } from "@app/features/active-orders/pages/active-order-ccform/active-order-ccform.component";
import { CountryregulationsManagerComponent } from '../Utilities/countryregulations_manager.component';

/* =============================
   CodeMirror (ADD ONLY)
   ============================= */
import { EditorState } from '@codemirror/state';
import { Decoration, EditorView, ViewPlugin, ViewUpdate, keymap } from '@codemirror/view';
import { sql as sqlLang } from '@codemirror/lang-sql';
import { oneDarkTheme } from '@codemirror/theme-one-dark';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { Subscription } from "rxjs";

interface JsonLine {
  kind: 'brace' | 'pair' | 'value' | 'content';
  indent: string;
  // brace
  text?: '{' | '}' | '[' | ']';
  // pair
  key?: string;
  // pair OR value
  value?: string;
  valueType?: 'string' | 'number' | 'boolean' | 'null';
  // content (base64 eye)
  base64?: string;
  preview?: string;
  fileName?: string;
  mimeType?: string;
  comma?: boolean;
}

type SqlTokenType = 'keyword' | 'table' | 'identifier' | 'operator' | 'string' | 'type' | 'plain';

interface SqlToken {
  start: number;
  end: number;
  text: string;
  type: SqlTokenType;
}

@Component({
  selector: 'app-sql-runner',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    AngularSplitModule,
    ScrollingModule,
    FloatingTooltipDirective
  ],
  templateUrl: './sql-runner.component.html',
  styleUrls: ['./sql-runner.component.scss']
})

export class SqlRunnerComponent implements AfterViewInit {

  public sql = "select * from CSDA_ShopifyDetails";

  private readonly DEFAULT_COLUMN_WIDTH = 200;
  private readonly MIN_COLUMN_WIDTH = 200;
  private gridSearchResults: { rowIndex: number; colIndex: number }[] = [];
  private gridSearchIndex = -1;
  private searchFocus: | { rowIndex: number; colIndex: number } | null = null;

  @ViewChild('resultTable') private resultTable!: ElementRef<HTMLTableElement>;
  @ViewChild('jsonTextContent') private jsonContent!: ElementRef<HTMLElement>;
  @ViewChild('xmlTextContent') private xmlContent!: ElementRef<HTMLElement>;
  @ViewChild('sqlEditor') private sqlEditor!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('sqlEditorHost') private sqlEditorHost!: ElementRef<HTMLDivElement>;
  @ViewChild('keyInput') keyInput!: ElementRef<HTMLInputElement>;
  @ViewChild(CdkVirtualScrollViewport)

  private viewport!: CdkVirtualScrollViewport;

  public readonly ROW_HEIGHT = 28;

  private cmEditor!: EditorView;

  public executionTimeMs = 0;
  public resultsExpanded = false;

  public toggleResultsExpanded(): void {
    this.resultsExpanded = !this.resultsExpanded;
    setTimeout(() => this.viewport?.checkViewportSize(), 0);
  }

  public async copyResults(): Promise<void> {
    if (!this.rows.length) return;
    const clean = (value: any): string => String(this.formatCellValue(value) ?? '').replace(/\r?\n/g, ' ');
    const content = [this.columns.join('\t'), ...this.rows.map(row => this.columns.map(col => clean(row[col])).join('\t'))].join('\n');
    await navigator.clipboard.writeText(content);
  }

  public exportResultsCsv(): void {
    if (!this.rows.length) return;
    const escape = (value: any): string => '"' + String(this.formatCellValue(value) ?? '').replace(/"/g, '""') + '"';
    const content = [this.columns.map(escape).join(','), ...this.rows.map(row => this.columns.map(col => escape(row[col])).join(','))].join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sql-results.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  public isProcessing = false;
  public tableReady = false;

  public rows: any[] = [];
  public columns: string[] = [];
  public columnWidths: number[] = [];
  public totalTableWidth = 0;
  public gridScrollLeft = 0;

  public syncGridHeader(event: Event): void {
    this.gridScrollLeft = (event.currentTarget as HTMLElement).scrollLeft;
  }

  // TABLE SEARCH
  public searchText = '';
  private searchResults: HTMLElement[] = [];
  private searchIndex = -1;

  // JSON SEARCH
  public jsonSearchText = '';
  private jsonSearchResults: HTMLElement[] = [];
  private jsonSearchIndex = -1;

  // XML SEARCH
  public xmlSearchText = '';
  private xmlSearchResults: HTMLElement[] = [];
  private xmlSearchIndex = -1;

  // JSON POPUP
  public jsonPopupVisible = false;
  public jsonPopupContent!: SafeHtml;
  public jsonLines: JsonLine[] = [];
  private jsonPopupData: any = null;

  // XML POPUP
  public xmlPopupVisible = false;
  public xmlPopupContent!: SafeHtml;

  // HTML POPUP
  public htmlPopupVisible = false;
  public htmlPopupContent!: SafeHtml;
  public shopifyKeyHeaderTooltip = "Shopify key used to authenticate.<br><span style='color:yellow'>Click on a cell to use the icon</span>";
  public priceBreakdownHeaderTooltip = "Price breakdown.<br><span style='color:yellow'>Click on a cell to use the icon</span>";

  // Shopify image POPUP
  public shopifyPreviewVisible = false;
  public shopifyPreviewImageUrl = '';
  public shopifyImageLoading = false;

  // RESIZE
  private resizingIndex: number | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  // COPY CELL
  public activeCell: { rowIndex: number; colIndex: number } | null = null;
  public copyFeedback = false;

  // =============================
  // BEFORE TOOLTIP
  // =============================
  public beforeTooltipVisible = false;
  public beforeTooltipText = '';
  public beforeTooltipX = 0;
  public beforeTooltipY = 0;
  public beforeTooltipMode: 'default' | 'query' = 'default';

  public warningCount = 0;
  private allRows: any[] = [];
  public showWarningsOnly = false;
  public pricePopupVisible = false;
  public pricePopupRow: any = null;

  // =============================
  // QUERY HISTORY (COOKIE)
  // =============================
  public queryHistory: string[] = [];
  public selectedQuery = '';
  public queryListOpen = false;
  public queryDropdownLeft = 0;
  public queryDropdownTop = 0;
  @ViewChild('queryPicker') private queryPicker?: ElementRef<HTMLElement>;
  public defaultQuery: string | null = null;
  public beforeTooltipHtml: SafeHtml = '';
  private executeSqlSubscription?: Subscription;

  private readonly QUERY_COOKIE = 'sql_runner_queries';
  private readonly MAX_QUERIES = 20;
  private readonly DEFAULT_QUERY_COOKIE = 'sql_runner_default_query';

  constructor(
    private httpClient: HttpClient,
    private validationService: ValidationService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private floatingTooltip: FloatingTooltipService
  ) { }

  /* =============================
     CodeMirror INIT (ADD ONLY)
     ============================= */
  public ngAfterViewInit(): void {
    this.loadQueryHistory();

    this.cmEditor = new EditorView({
      parent: this.sqlEditorHost.nativeElement,
      state: EditorState.create({
        doc: this.sql,
        extensions: [
          history(),
          keymap.of([
            ...defaultKeymap,
            ...historyKeymap
          ]),
          sqlLang(),
          oneDarkTheme,
          this.sqlColorPlugin,
          EditorView.updateListener.of(update => {
            if (update.docChanged) {
              this.sql = update.state.doc.toString();
            }
          })
        ]
      })
    });

    if (this.defaultQuery) {
      this.cmEditor.dispatch({
        changes: {
          from: 0,
          to: this.cmEditor.state.doc.length,
          insert: this.defaultQuery
        }
      });
      this.sql = this.defaultQuery;
    }

    this.injectEditorSaveButton();
  }

  private readonly sqlColorPlugin = (() => {
    const host = this;

    return ViewPlugin.fromClass(class {
      decorations;

      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged)
          this.decorations = this.buildDecorations(update.view);
      }

      buildDecorations(view: EditorView) {
        const keywordDeco = Decoration.mark({ class: 'cm-sql-keyword' });
        const tableDeco = Decoration.mark({ class: 'cm-sql-table' });
        const identifierDeco = Decoration.mark({ class: 'cm-sql-identifier' });
        const operatorDeco = Decoration.mark({ class: 'cm-sql-operator' });
        const stringDeco = Decoration.mark({ class: 'cm-sql-string' });
        const typeDeco = Decoration.mark({ class: 'cm-sql-type' });

        const ranges: any[] = [];

        for (const token of host.tokenizeSql(view.state.doc.toString())) {
          if (token.type === 'keyword') ranges.push(keywordDeco.range(token.start, token.end));
          if (token.type === 'table') ranges.push(tableDeco.range(token.start, token.end));
          if (token.type === 'identifier') ranges.push(identifierDeco.range(token.start, token.end));
          if (token.type === 'operator') ranges.push(operatorDeco.range(token.start, token.end));
          if (token.type === 'string') ranges.push(stringDeco.range(token.start, token.end));
          if (token.type === 'type') ranges.push(typeDeco.range(token.start, token.end));
        }

        return Decoration.set(ranges, true);
      }
    }, {
      decorations: v => v.decorations
    });
  })(); 

  openActiveOrderCCForm(orderNo: number): void {
    this.dialog.open(ActiveOrderCCFormComponent, {
      width: 'auto',
      maxWidth: 'none',
      panelClass: 'active-order-dialog',
      data: { orderNo }
    });
  }

openCountryRegulationsManager(): void {
  const activeElement = document.activeElement;

  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }

  requestAnimationFrame(() => {
    this.dialog.open(CountryregulationsManagerComponent, {
      width: '1100px',
      maxWidth: '98vw',
      maxHeight: '85vh',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      panelClass: 'country-regulations-dialog'
    });
  });
}

  public isActiveRow(rowIndex: number): boolean {
    return this.activeCell?.rowIndex === rowIndex;
  }

  public toggleDefaultQuery(sql: string, event: MouseEvent): void {
    event.stopPropagation();

    if (this.defaultQuery === sql) {
      this.defaultQuery = null;
      document.cookie = `${this.DEFAULT_QUERY_COOKIE}=; path=/; max-age=0`;
      return;
    }

    this.defaultQuery = sql;
    document.cookie =
      `${this.DEFAULT_QUERY_COOKIE}=${encodeURIComponent(sql)}; path=/; max-age=31536000`;
  }

  public hasBase64Document(value: any): boolean {
    if (typeof value !== 'string') return false;
    return !!this.detectBase64Document(value);
  }

  public openBase64Preview(value: string, event: MouseEvent): void {
    event.stopPropagation();
    const detected = this.detectBase64Document(value);
    if (!detected) return;
    this.previewBase64Document(detected.base64, detected.mimeType);
  }
  
  openCopyOrderDialog() {
    this.dialog.open(CopyOrderDialogComponent, {
      width: '420px',
      maxWidth: '420px',
      panelClass: 'copy-order-dialog'
    });
  }

  private injectEditorSaveButton(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scroller = this.cmEditor.dom.querySelector('.cm-scroller') as HTMLElement | null;
        if (!scroller) {
          console.warn('cm-scroller not found');
          return;
        }

        // Prevent double injection
        if (scroller.querySelector('.cm-save-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'cm-save-btn';
        btn.type = 'button';

        btn.innerHTML = `
          <span class="cm-save-icon material-icons">save</span>
        `;

        this.floatingTooltip.attach(btn, 'Save query in queries list');

        btn.addEventListener('click', (e) => {
          e.stopPropagation();

          this.saveCurrentQuery();

          btn.classList.remove('saved');
          void btn.offsetWidth; // force reflow
          btn.classList.add('saved');

          this.playCopySound();
        });
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.saveCurrentQuery();
        });

        scroller.appendChild(btn);
      });
    });
  }

  public saveCurrentQuery(): void {
    const sql = this.cmEditor
      ? this.cmEditor.state.doc.toString().trim()
      : this.sql.trim();

    if (!sql) {
      Swal.fire('', 'Nothing to save', 'info');
      return;
    }

    this.addQueryToHistory(sql);
  }

  shoKeyPopoverVisible = false;
  shoKeyPopoverX = 0;
  shoKeyPopoverY = 0;
  shoKeyValue = '';

  closeShoKeyPopover(): void {
    (document.activeElement as HTMLElement | null)?.blur(); this.shoKeyPopoverVisible = false;
  }

  openShoKeyPopover(event: MouseEvent, row: any): void {
    event.stopPropagation();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.shoKeyPopoverX = rect.left;
    this.shoKeyPopoverY = rect.bottom + 6;
    this.shoKeyValue = (row as any).__shoKey;
    this.shoKeyPopoverVisible = true;
  }

  copyShoKey(): void {
    navigator.clipboard.writeText(this.shoKeyValue);
    this.shoKeyPopoverVisible = false;
  }

  // =============================
  // QUERY HISTORY (COOKIE)
  // =============================
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.query-picker')) {
      this.queryListOpen = false;
    }
  }
  
  public parseQuery(sql: string): { before: string; table: string; after: boolean } | null {
    if (!sql) return null;

    const normalized = sql.replace(/\s+/g, ' ').trim();

    const match = normalized.match(
      /^(select\b.*?\bfrom\b\s+)([^\s,;]+)(.*)$/i
    );

    if (!match) {
      return {
        before: normalized,
        table: '',
        after: false
      };
    }

    const [, before, table, rest] = match;

    return {
      before,
      table,
      after: rest.trim().length > 0
    };
  }

  public getQueryLabel(sql: string): SafeHtml {
    if (!sql) return '';

    const normalized = sql.replace(/\s+/g, ' ').trim();

    const match = normalized.match(
      /^(select\b.*?\bfrom\b\s+)([^\s,;]+)(.*)$/i
    );

    let html: string;

    if (match) {
      const [, selectFrom, table, rest] = match;
      const hasMore = rest.trim().length > 0;

      html =
        `${selectFrom}<span class="query-table">${table}</span>` +
        (hasMore ? ' …' : '');
    } else {
      html =
        normalized.length > 80
          ? normalized.slice(0, 80) + '…'
          : normalized;
    }

    // 🔒 Explicitly trust this HTML
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public toggleQueryList(): void {
    if (!this.queryListOpen && this.queryPicker) {
      const rect = this.queryPicker.nativeElement.getBoundingClientRect();
      this.queryDropdownLeft = rect.left;
      this.queryDropdownTop = rect.bottom + 6;
    }
    this.queryListOpen = !this.queryListOpen;
  }

  public closeQueryList(): void {
    this.queryListOpen = false;
    this.hideBeforeTooltip();
  }

  public applyQuery(sql: string): void {
    if (!this.cmEditor) return;

    this.cmEditor.dispatch({
      changes: {
        from: 0,
        to: this.cmEditor.state.doc.length,
        insert: sql
      }
    });

    this.sql = sql;
    this.queryListOpen = false;
    this.hideBeforeTooltip();
  }

  public deleteQuery(sql: string, event: MouseEvent): void {
    event.stopPropagation();

    if (this.defaultQuery === sql) {
      this.defaultQuery = null;
      document.cookie = `${this.DEFAULT_QUERY_COOKIE}=; path=/; max-age=0`;
    }

    this.queryHistory = this.queryHistory.filter(q => q !== sql);
    this.saveQueryHistory();
    this.hideBeforeTooltip();
  }

  public showQueryTooltip(event: MouseEvent, sql: string): void {
    if (!sql?.trim()) return;
    this.beforeTooltipMode = 'query';
    this.beforeTooltipText = sql.replace(/^\s+/, '');
    this.beforeTooltipHtml = this.buildQueryTooltipHtml(this.beforeTooltipText);
    this.beforeTooltipVisible = true;
    this.moveTooltip(event);
  }

  private readonly sqlKeywords = new Set([
    'SELECT', 'FROM', 'WHERE', 'ORDER', 'BY', 'GROUP', 'HAVING', 'AND', 'OR', 'AS', 'TOP', 'DISTINCT',
    'CAST', 'DESC', 'ASC', 'NOT', 'LIKE', 'IN', 'IS', 'NULL', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER',
    'ON', 'UPDATE', 'SET', 'INSERT', 'INTO', 'DELETE', 'VALUES', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
    'GETDATE'
  ]);

  private tokenizeSql(sql: string): SqlToken[] {
    const tokens: SqlToken[] = [];
    const regex = /'[^']*'|>=|<=|<>|!=|=|>|<|\*|-\d+|\+\d+|\b\d+\b|\b[A-Za-z_][A-Za-z0-9_]*\b|\s+|./gi;
    let match: RegExpExecArray | null;
    let expectTable = false;
    let previousKeyword = '';

    while ((match = regex.exec(sql)) !== null) {
      const text = match[0];
      const upper = text.toUpperCase();
      const start = match.index;
      const end = start + text.length;

      if (/^\s+$/.test(text)) {
        tokens.push({ start, end, text, type: 'plain' });
        continue;
      }

      if (/^'[^']*'$/.test(text)) {
        tokens.push({ start, end, text, type: 'string' });
        continue;
      }

      if (/^(>=|<=|<>|!=|=|>|<|\*|-\d+|\+\d+|\d+)$/.test(text)) {
        tokens.push({ start, end, text, type: 'operator' });
        continue;
      }

      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(text)) {
        if (this.sqlKeywords.has(upper)) {
          tokens.push({ start, end, text, type: 'keyword' });

          if (upper === 'FROM' || upper === 'JOIN' || upper === 'INTO' || upper === 'UPDATE') {
            expectTable = true;
          } else if (upper === 'DELETE') {
            previousKeyword = 'DELETE';
          } else if (upper === 'FROM' && previousKeyword === 'DELETE') {
            expectTable = true;
          } else {
            previousKeyword = upper;
          }

          continue;
        }

        if (expectTable) {
          tokens.push({ start, end, text, type: 'table' });
          expectTable = false;
          previousKeyword = '';
          continue;
        }

        if (upper === 'DATE') {
          tokens.push({ start, end, text, type: 'type' });
          continue;
        }

        tokens.push({ start, end, text, type: 'identifier' });
        continue;
      }

      tokens.push({ start, end, text, type: 'plain' });
    }

    return tokens;
  }

  private buildQueryTooltipHtml(sql: string): SafeHtml {
    const escapeHtml = (value: string): string =>
      value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const html = this.tokenizeSql(sql).map(token => {
      const text = escapeHtml(token.text);

      if (token.type === 'keyword') return `<span class="query-tt-keyword">${text}</span>`;
      if (token.type === 'table') return `<span class="query-tt-table">${text}</span>`;
      if (token.type === 'identifier') return `<span class="query-tt-field">${text}</span>`;
      if (token.type === 'operator') return `<span class="query-tt-operator">${text}</span>`;
      if (token.type === 'string') return `<span class="query-tt-string">${text}</span>`;
      if (token.type === 'type') return `<span class="query-tt-type">${text}</span>`;
      return text;
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private loadQueryHistory(): void {
    const match = document.cookie
      .split('; ')
      .find(c => c.startsWith(this.QUERY_COOKIE + '='));

    if (!match) return;

    try {
      const decoded = decodeURIComponent(match.split('=')[1]);
      const parsed = JSON.parse(decoded);

      if (Array.isArray(parsed)) {
        this.queryHistory = parsed;
      }

      const def = document.cookie
        .split('; ')
        .find(c => c.startsWith(this.DEFAULT_QUERY_COOKIE + '='));

      if (def) {
        this.defaultQuery = decodeURIComponent(def.split('=')[1]);
      }
    } catch {
      this.queryHistory = [];
    }
  }

  private saveQueryHistory(): void {
    const value = encodeURIComponent(JSON.stringify(this.queryHistory));
    document.cookie = `${this.QUERY_COOKIE}=${value}; path=/; max-age=31536000`;
  }

  private addQueryToHistory(sql: string): void {
    const normalized = sql.trim();
    if (!normalized) return;

    // Remove if already exists
    this.queryHistory = this.queryHistory.filter(q => q !== normalized);

    // Add to top
    this.queryHistory.unshift(normalized);

    // Limit size
    if (this.queryHistory.length > this.MAX_QUERIES) {
      this.queryHistory.length = this.MAX_QUERIES;
    }

    this.saveQueryHistory();
  }

  public onQuerySelected(): void {
    if (!this.selectedQuery || !this.cmEditor) return;

    const sql = this.selectedQuery;

    this.cmEditor.dispatch({
      changes: {
        from: 0,
        to: this.cmEditor.state.doc.length,
        insert: sql
      }
    });

    this.sql = sql;

    // 🔒 reset dropdown back to placeholder
    setTimeout(() => {
      this.selectedQuery = '';
    });
  }

  public deleteSelectedQuery(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.selectedQuery) return;

    this.queryHistory = this.queryHistory.filter(q => q !== this.selectedQuery);
    this.selectedQuery = '';
    this.saveQueryHistory();
  }

  public openShopifyVariantPreview(variantId: string): void {
    this.isProcessing = true;
    this.shopifyPreviewVisible = false;
    this.shopifyPreviewImageUrl = '';

    this.httpClient
      .get(environment.CSDAEndPoint + '/CSDA/GetShopifyImageURL', {
        params: { VariantId: variantId },
        responseType: 'text'
      })
      .subscribe({
        next: imageUrl => {
          this.preloadShopifyImage(imageUrl);
        },
        error: err => {
          this.isProcessing = false;
          console.error(err);
          Swal.fire('Error', 'Unable to load product image', 'error');
        }
      });
  }
  
  private preloadShopifyImage(imageUrl: string): void {
    const img = new Image();

    img.onload = () => {
      this.shopifyPreviewImageUrl = imageUrl;
      this.isProcessing = false;
      this.shopifyPreviewVisible = true;
    };

    img.onerror = () => {
      this.isProcessing = false;
      Swal.fire('', 'Unable to load product image', 'error');
    };

    // 🔑 MUST be last
    img.src = imageUrl;
  }

  public closeShopifyPreview(): void {
    this.shopifyPreviewVisible = false;
    this.shopifyPreviewImageUrl = '';
  }

  public onShopifyImageLoaded(): void {
    this.isProcessing = false;
    this.shopifyPreviewVisible = true;
  }

  public onShopifyImageError(): void {
    this.shopifyImageLoading = false;
    this.isProcessing = false;
    this.shopifyPreviewVisible = false;
    this.shopifyPreviewImageUrl = '';
    Swal.fire('', 'Unable to load product image', 'error');
  }

  public openPricePopup(row: any, event: MouseEvent): void {
    event.stopPropagation();
    this.pricePopupRow = row;
    this.pricePopupVisible = true;
  }

  public closePricePopup(): void {
    this.pricePopupVisible = false;
    this.pricePopupRow = null;
  }

  public buildPriceBreakdown(row: any): string {
    const safeParse = (value: any): any => {
      if (typeof value !== 'string') return null;
      try {
        return JSON.parse(value.replace(/,\s*(\}|\])/g, '$1'));
      } catch {
        return null;
      }
    };

    const formatMoney = (value: any): string => typeof value === 'number' && !isNaN(value) ? `$${value.toFixed(2)}` : value ?? 'N/A';

    const cubi = safeParse(row?.SHO_CubiResponse);
    const shopify = safeParse(row?.SHO_ShopifyResponse);
    const products = safeParse(row?.SHO_ProductDetails);

    const lines: string[] = [];
    const LABEL_WIDTH = 34;

    const formatLine = (label: string, value: any): string => `${label.padEnd(LABEL_WIDTH, ' ')}: ${formatMoney(value)}`;

    //----------------------------------
    // CUBI → QUOTE / PRICE
    //----------------------------------
    const quote = cubi?.QuoteResults?.[0]?.Quotes?.[0];
    const price = quote?.Price;

    //----------------------------------
    // Base Price
    //----------------------------------
    lines.push(`Base Price: ${formatMoney(price?.BasePrice)}`);
    lines.push('');

    //----------------------------------
    // Surcharges
    //----------------------------------
    if (Array.isArray(price?.Surcharges) && price.Surcharges.length) {
      lines.push('Surcharges:');

      for (const s of price.Surcharges) {
        lines.push(`  Amount      : ${formatMoney(Number(s.Amount))}`);
        lines.push(`  Description : ${s.Description}`);
        lines.push('');
      }
    }

    //----------------------------------
    // Taxes
    //----------------------------------
    if (Array.isArray(price?.Taxes) && price.Taxes.length) {
      lines.push('Taxes:');

      for (const t of price.Taxes) {
        lines.push(`  Amount      : ${formatMoney(Number(t.Amount))}`);
        lines.push(`  Description : ${t.Description}`);
        lines.push('');
      }
    }

    //----------------------------------
    // Price Breakdown
    //----------------------------------
    const shippingCost = price?.TotalAmount;
    
    //-----------------------------------------------------------------------------------------------
    // Demande de Morgane Jaugeas de chez Galerie Beauchamp pour que si la hauteur 2026-01-16
    // et la largeur est la même pour plusieur toiles, de prendre la surcharge la plus élevé du goupe
    //-----------------------------------------------------------------------------------------------
    const shopifySurchargeTotal =
      Array.isArray(products)
        ? (() => {
            const map = new Map<string, number>();

            for (const p of products) {
              const width = Number(p?.Package?.Width ?? 0);
              const height = Number(p?.Package?.Height ?? 0);
              const quantity = Number(p?.Package?.Quantity ?? 1);
              const surcharge = quantity * Number(p?.Product?.Pro_ShopifySurcharge ?? 0);

              const key = `${width}x${height}`;
              const currentMax = map.get(key) ?? 0;

              if (surcharge > currentMax) {
                map.set(key, surcharge);
              }
            }

            return Array.from(map.values()).reduce((sum, v) => sum + v, 0);
          })()
        : null;

    const rateWithoutInsurance = shopify?.rates?.find((r: any) => r.service_name?.includes('NOT_INSURED'));
    const totalWithoutInsurance = rateWithoutInsurance ? Number(rateWithoutInsurance.total_price) / 100 : null;
    const globexInsurance = typeof shopify?.GlobexInsurance === 'number' ? shopify.GlobexInsurance : 0;
    const totalWithInsurance = totalWithoutInsurance !== null ? totalWithoutInsurance + globexInsurance : null;

    //----------------------------------
    // Final total lines
    //----------------------------------
    lines.push(formatLine('Shipping Cost', shippingCost));
    lines.push(formatLine('Shopify Surcharge total', shopifySurchargeTotal));
    lines.push(formatLine('Total without insurance', totalWithoutInsurance));
    lines.push(
      `${'Total with Globex insurance'.padEnd(LABEL_WIDTH, ' ')}: ` +
      `${formatMoney(totalWithoutInsurance)} + ${formatMoney(globexInsurance)} = ${formatMoney(totalWithInsurance)}`
    );

    return lines.join('\n').trim();
  }

  private buildPriceBreakdownHtml(row: any): string {
    const esc = (v: any) =>
      String(v ?? 'N/A')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const format2 = (v: any): string =>
      typeof v === 'number' && !isNaN(v)
        ? v.toFixed(2)
        : 'N/A';

    const money = (v: any) =>
      v === 'N/A' || v === null || v === undefined
        ? 'N/A'
        : `$${format2(Number(v))}`;

    const safeParse = (v: any): any => {
      if (typeof v !== 'string') return null;
      try {
        return JSON.parse(v.replace(/,\s*(\}|\])/g, '$1'));
      } catch {
        return null;
      }
    };

    const cubi = safeParse(row?.SHO_CubiResponse);
    const shopify = safeParse(row?.SHO_ShopifyResponse);
    const products = safeParse(row?.SHO_ProductDetails);

    const quote = cubi?.QuoteResults?.[0]?.Quotes?.[0];
    const price = quote?.Price;

    //-----------------------------------------------------------------------------------------------
    // Demande de Morgane Jaugeas de chez Galerie Beauchamp pour que si la hauteur 2026-01-16
    // et la largeur est la même pour plusieur toiles, de prendre la surcharge la plus élevé du goupe
    //-----------------------------------------------------------------------------------------------
    const shopifySurchargeTotal =
      Array.isArray(products)
        ? (() => {
            const map = new Map<string, number>();

            for (const p of products) {
              const width = Number(p?.Package?.Width ?? 0);
              const height = Number(p?.Package?.Height ?? 0);
              const quantity = Number(p?.Package?.Quantity ?? 1);
              const surcharge = quantity * Number(p?.Product?.Pro_ShopifySurcharge ?? 0);

              const key = `${width}x${height}`;
              const currentMax = map.get(key) ?? 0;

              if (surcharge > currentMax) {
                map.set(key, surcharge);
              }
            }

            return Array.from(map.values()).reduce((sum, v) => sum + v, 0);
          })()
        : 'N/A';

    const rateWithoutInsurance =
      shopify?.rates?.find((r: any) =>
        r.service_name?.includes('NOT_INSURED')
      );

    const totalWithoutInsurance =
      rateWithoutInsurance
        ? Number(rateWithoutInsurance.total_price) / 100
        : 'N/A';

    const globexInsurance = shopify?.GlobexInsurance ?? 0;
    const totalWithInsurance =
      typeof totalWithoutInsurance === 'number'
        ? totalWithoutInsurance + globexInsurance
        : 'N/A';

    let html = `
      <table cellpadding="0" cellspacing="0"
        style="
          border-collapse:collapse;
          font-family:Aptos, Segoe UI, Arial, sans-serif;
          font-size:12px;
          color:#000;
          width:auto;
        ">
      `;

    const border = 'border:1px solid #505050;';
    const dots = '..........';

    const rowHtml = (label: string, value: string, bold = false) => `
      <tr>
        <td style="${border} padding:4px 8px; font-weight:${bold ? '600' : '400'}; white-space:nowrap;">
          ${label}
        </td>
        <td style="${border} padding:4px 8px;">
          ${value}
        </td>
      </tr>`;

    const dottedRow = (label: string, value: string) =>
      rowHtml(`${dots} ${label}`, value);

    const section = (title: string) => `
      <tr>
        <td colspan="2"
            style="
              ${border}
              background:#9fe3f9;
              font-weight:600;
              padding:6px 8px;
            ">
          ${title}
        </td>
      </tr>`;

    /* ===== CONTENT ===== */

    html += rowHtml('Base Price', money(price?.BasePrice), true);

    if (price?.Surcharges?.length) {
      html += section('Surcharges');
      for (const s of price.Surcharges) {
        html += dottedRow(esc(s.Description), money(s.Amount));
      }
    }

    if (price?.Taxes?.length) {
      html += section('Taxes');
      for (const t of price.Taxes) {
        html += dottedRow(esc(t.Description), money(t.Amount));
      }
    }

    html += section('Totals');
    html += rowHtml('Shipping Cost', money(price?.TotalAmount));
    html += rowHtml('Shopify Surcharge total', money(shopifySurchargeTotal));
    html += rowHtml('Total without insurance', money(totalWithoutInsurance));
    html += rowHtml(
      'Total with Globex insurance',
      `${money(totalWithoutInsurance)} + ${money(globexInsurance)} = ${money(totalWithInsurance)}`
    );

    html += '</table>';

    return html;
  }

  public copyPriceBreakdown(): void {
    if (!this.pricePopupRow) return;

    const text = this.buildPriceBreakdown(this.pricePopupRow);
    const html = this.buildPriceBreakdownHtml(this.pricePopupRow);

    const item = new ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' })
    });

    navigator.clipboard.write([item]).then(() => {
      this.playCopySound();
    });
  }

  private playCopySound(): void {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 880; // 🔊 pleasant confirmation tone
      gain.gain.value = 0.15;

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start();

      // short, clean "tick"
      setTimeout(() => {
        oscillator.stop();
        ctx.close();
      }, 120);
    } catch {
      // silently ignore (sound is non-critical)
    }
  }

  public showBeforeTooltip(event: MouseEvent, col: string, value: any): void {
    if (!this.isResponseColumn(col)) return;
    if (!this.hasBeforeValue(value)) return;

    this.beforeTooltipMode = 'default';
    this.beforeTooltipText = 'Before calling agent or executing code';
    this.beforeTooltipVisible = true;
    this.moveTooltip(event);
  }

  public hideBeforeTooltip(): void {
    this.beforeTooltipVisible = false;
    this.beforeTooltipMode = 'default';
  }

public moveTooltip(event: MouseEvent): void {
  const offsetX = 14;
  const offsetY = 18;
  const tooltipWidth = this.beforeTooltipMode === 'query'
    ? Math.min(window.innerWidth - 24, Math.max(260, this.beforeTooltipText.split('\n').reduce((m, line) => Math.max(m, line.length), 0) * 9 + 24))
    : 260;
  const tooltipHeight = this.beforeTooltipMode === 'query' ? 220 : 60;
  const maxX = window.innerWidth - tooltipWidth - 12;
  const maxY = window.innerHeight - tooltipHeight - 12;

  this.beforeTooltipX = Math.max(12, Math.min(event.clientX + offsetX, maxX));
  this.beforeTooltipY = Math.max(12, Math.min(event.clientY + offsetY, maxY));
}

  private openBase64File(base64: string, fileName?: string): void {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    let mimeType = this.getMimeType(fileName);

    // HARD GUARANTEE: preview PDFs inline
    if (!mimeType || mimeType === 'application/octet-stream') {
      mimeType = 'application/pdf';
    }

    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);

    window.open(url, '_blank');
  }

  // =============================
  // LOAD SQL FILE
  // =============================

  public loadSqlFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const file = input.files[0];
    if (!file.name.toLowerCase().endsWith('.sql')) {
      Swal.fire('', 'Please select a .sql file', 'error');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      this.sql = text;

      if (this.cmEditor) {
        this.cmEditor.dispatch({
          changes: {
            from: 0,
            to: this.cmEditor.state.doc.length,
            insert: text
          }
        });
      }
    };
    reader.readAsText(file);
    input.value = '';
  }

  // =============================
  // SAVE SQL FILE
  // =============================

  public async saveSqlFile(): Promise<void> {
    const content = this.cmEditor ? this.cmEditor.state.doc.toString() : this.sql;

    if (!content.trim()) {
      Swal.fire('', 'Nothing to save', 'info');
      return;
    }

    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: 'SqlQuery.sql',
        types: [
          {
            description: 'SQL files',
            accept: { 'application/sql': ['.sql'] }
          }
        ]
      });

      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        Swal.fire('', 'Failed to save file', 'error');
      }
    }
  }

  get allCount(): number {
    return this.allRows.length;
  }

  //--------------------------------------------------------------------------------------------------------------
  keyValue = '';
  showKey = false;
  selectedServer = 0;
  readonly serverOptions = [
    { code: 0, label: 'CUBI' },
    { code: 1, label: 'GLOBEX' }
  ];
  authState: 'idle' | 'ok' | 'error' = 'idle';
  shakeOffset = 'translateX(0)';
  isAuthorizedSession = sessionStorage.getItem('sqlAuth') === '1';

  toggleShowKey(): void {
    this.showKey = !this.showKey;
  }

  private triggerShake(): void {
    this.authState = 'error';
    const offsets = [-6, 6, -6, 6, 0];
    offsets.forEach((x, i) => {
      setTimeout(() => {
        this.shakeOffset = `translateX(${x}px)`;
      }, i * 50);
    });
  }

  async isAuthorized(): Promise<boolean> {
    if (this.isAuthorizedSession) {
      this.authState = 'ok';
      return true;
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(this.keyValue);
    const buffer = await crypto.subtle.digest('SHA-256', data);
    const hash = Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    const ok = hash === '227ae37684d3f170ecd47ca9ad386cb2ee75ce906cb27e07c0f6a85a6a611f34';

    if (!ok) {
      this.triggerShake();
      return false;
    }

    sessionStorage.setItem('sqlAuth', '1');
    this.isAuthorizedSession = true;
    this.authState = 'ok';
    return true;
  }
  //--------------------------------------------------------------------------------------------------------------

  // =============================
  // EXECUTE SQL
  // =============================
  public async executeSql(sqlOverride?: string): Promise<void> {
    const authorized = await this.isAuthorized();

    if (!authorized) {
      setTimeout(() => {
        Swal.fire({
          icon: 'error',
          title: 'You are not authorized',
          didClose: () => {
            setTimeout(() => {
              this.keyInput?.nativeElement.focus();
            }, 100);
          }
        });
      }, 300);
      return;
    }

    if (this.executeSqlSubscription && !this.executeSqlSubscription.closed)
      this.executeSqlSubscription.unsubscribe();

    this.showWarningsOnly = false;
  const sqlToRun = (sqlOverride ?? this.sql).trim();
  if (!sqlToRun) return;

  this.isProcessing = true;
  this.executionTimeMs = 0;
  const executionStartedAt = performance.now();
  this.tableReady = false;

  const detail: ExecuteSqlDetail = {
    Language: Language.English,
    IsDevelopmentEnvironment: false,
    Server: this.selectedServer,
    Sql: sqlToRun
  };

  const endpoint = environment.CSDAEndPoint + '/CSDA/ExecuteSql';

  const escapeHtml = (value: any): string => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const stringifySafe = (value: any): string => {
    try {
      if (typeof value === 'string') return value;
      return JSON.stringify(value, Object.getOwnPropertyNames(value ?? {}), 2);
    } catch {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
  };

  this.executeSqlSubscription = this.httpClient
    .post<ExecuteSqlResult>(endpoint, detail, { observe: 'response' })
    .pipe(
      map(response => {
        console.log('ExecuteSql HTTP response:', response);

        const data = response.body;
        const validation = this.validationService.validate(data);

        if (!validation.isValid) {
          Swal.fire('', validation.message, 'error');
          return null;
        }

        if (validation.message)
          Swal.fire('', validation.message, 'info');

        return data;
      }),
      finalize(() => {
        this.executionTimeMs = Math.round(performance.now() - executionStartedAt);
        this.isProcessing = false;
        this.executeSqlSubscription = undefined;
      })
    )
    .subscribe({
      next: result => {
        if (!result?.Data?.length) {
          this.rows = [];
          this.columns = [];
          this.columnWidths = [];
          this.tableReady = false;
          return;
        }

        this.allRows = result.Data.map(r => {
          const { SHO_CubiRequestHash, SHO_Key, ...rest } = r as any;
          Object.defineProperty(rest, '__shoKey', {
            value: SHO_Key ?? '',
            enumerable: false,
            writable: false,
            configurable: false
          });
          return rest;
        });

        this.rows = [...this.allRows];
        this.columns = Object.keys(this.rows[0]);
        this.warningCount = this.computeHtmlWarnings(this.rows, this.columns);

        this.columnWidths = this.columns.map(col =>
          this.getMinimumColumnWidth(col)
        );
        
        this.recalculateTableWidth();
        this.tableReady = true;
        this.clearSearch();
        setTimeout(() => {
          this.viewport?.checkViewportSize();
        }, 0);
        this.searchFocus = null;
        this.gridSearchResults = [];
        this.gridSearchIndex = -1;
      },
      error: err => {
        console.error('ExecuteSql full error:', err);
        console.error('ExecuteSql status:', err?.status);
        console.error('ExecuteSql message:', err?.message);
        console.error('ExecuteSql error body:', err?.error);
        console.error('ExecuteSql headers:', err?.headers);
        console.error('ExecuteSql stack:', err?.stack);

        const status = err?.status ?? '(none)';
        const statusText = err?.statusText ?? '';
        const name = err?.name ?? '';
        const message =
          err?.error?.Message ||
          err?.error?.message ||
          err?.error?.ExceptionMessage ||
          err?.message ||
          'Unknown error';
        const stack = err?.stack ?? '(no stack)';
        const rawError = stringifySafe(err?.error);
        const rawHttpError = stringifySafe(err);
        const responseHeaders = err?.headers?.keys
          ? err.headers.keys().map((k: string) => `${k}: ${err.headers.get(k)}`).join('\n')
          : '(no headers)';

        Swal.fire({
          icon: 'error',
          title: 'ExecuteSql error',
          width: 1100,
          heightAuto: false,
          // customClass: {
          //   popup: 'sql-error-swal-popup'
          // },          
          html: `
            <div style="text-align:left;font-family:Consolas,monospace;font-size:13px;word-break:break-word;max-height:60vh;overflow:auto;padding-right:10px;border: 1px solid black;">
              <div><b>Endpoint:</b> ${escapeHtml(endpoint)}</div>
              <div><b>Status:</b> ${escapeHtml(status)}</div>
              <div><b>StatusText:</b> ${escapeHtml(statusText)}</div>
              <div><b>Name:</b> ${escapeHtml(name)}</div>

              <div style="margin-top:4px;"><b>Request body:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(JSON.stringify(detail, null, 2))}</pre>

              <div style="margin-top:4px;"><b>Message:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(message)}</pre>

              <div style="margin-top:4px;"><b>Response headers:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(responseHeaders)}</pre>

              <div style="margin-top:4px;"><b>err.error:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(rawError)}</pre>

              <div style="margin-top:4px;"><b>Full error object:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(rawHttpError)}</pre>

              <div style="margin-top:4px;"><b>Stack:</b></div>
              <pre style="white-space:pre-wrap;">${escapeHtml(stack)}</pre>
            </div>
          `
        });
      }
    });
}

  public stopExecuteSql(): void {
    if (!this.executeSqlSubscription || this.executeSqlSubscription.closed) return;

    this.executeSqlSubscription.unsubscribe();
    this.isProcessing = false;
  }

  private computeHtmlWarnings(rows: any[], columns: string[]): number {
    let count = 0;

    for (const row of rows) {
      for (const col of columns) {
        if (this.isHtml(row[col])) {
          count++;
        }
      }
    }

    return count;
  }

  public toggleWarningFilter(): void {
    this.showWarningsOnly = !this.showWarningsOnly;

    if (!this.showWarningsOnly) {
      this.rows = [...this.allRows];
      this.viewport?.scrollToIndex(0);
      return;
    }

    this.rows = this.allRows.filter(row =>
      this.columns.some(col => this.isHtml(row[col]))
    );

    this.viewport?.scrollToIndex(0);
  }

  // =============================
  // COLUMN RESIZE
  // =============================

  private getMinimumColumnWidth(columnName: string): number {
    const textWidth = columnName.length * 8;
    const iconSpace = 28;
    const padding = 12;

    return Math.max(
      this.MIN_COLUMN_WIDTH,
      textWidth + iconSpace + padding
    );
  }

  public startResize(event: MouseEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();

    this.resizingIndex = index;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.columnWidths[index];
  }

  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(event: MouseEvent): void {
    if (this.resizingIndex === null) return;

    const delta = event.clientX - this.resizeStartX;

    const calculatedMinimumWidth = this.getMinimumColumnWidth(
      this.columns[this.resizingIndex]
    );

    const newWidth = Math.max(
      calculatedMinimumWidth,
      this.resizeStartWidth + delta
    );

    this.columnWidths[this.resizingIndex] = newWidth;
    this.recalculateTableWidth();
  }

  @HostListener('document:mouseup')
  public onMouseUp(): void {
    this.resizingIndex = null;
  }

  private recalculateTableWidth(): void {
    this.totalTableWidth = this.columnWidths.reduce((a, b) => a + b, 0);
  }

  // =============================
  // COPY CELL
  // =============================

  public activateCell(rowIndex: number, colIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    this.activeCell = { rowIndex, colIndex };
  }

  public copyCell(value: any, event: MouseEvent): void {
    event.stopPropagation();

    let textToCopy = '';

    // If JSON → pretty print like viewer
    if (this.isJson(value)) {
      try {
        const sanitized = value.replace(/,\s*(\}|\])/g, '$1');
        const parsed = JSON.parse(sanitized);
        textToCopy = JSON.stringify(parsed, null, 2);
      } catch {
        // fallback to raw
        textToCopy = String(value);
      }
    } else {
      // Non-JSON → raw
      textToCopy = String(value);
    }

    navigator.clipboard.writeText(textToCopy);

    this.copyFeedback = true;
    this.playCopySound();
    setTimeout(() => (this.copyFeedback = false), 1000);
  }

  // =============================
  // TABLE SEARCH
  // =============================
  private startSearch(): void {
    this.searchFocus = null;

    this.gridSearchResults = [];
    this.gridSearchIndex = -1;

    if (!this.searchText) return;

    const needle = this.searchText.toLowerCase();

    this.rows.forEach((row, rowIndex) => {
      this.columns.forEach((col, colIndex) => {
        const value = row[col];
        if (
          value != null &&
          String(value).toLowerCase().includes(needle)
        ) {
          this.gridSearchResults.push({ rowIndex, colIndex });
        }
      });
    });

    if (this.gridSearchResults.length) {
      this.gridSearchIndex = 0;
      this.focusGridResult();
    }
  }

  private focusGridResult(): void {
    const hit = this.gridSearchResults[this.gridSearchIndex];
    if (!hit) return;

    // 🔹 Set SEARCH focus (not active cell)
    this.searchFocus = hit;

    // 🔹 Scroll to row
    this.viewport.scrollToIndex(hit.rowIndex, 'smooth');
  }

  private focusResult(): void {
    this.searchResults.forEach(el => el.classList.remove('search-hit'));
    const el = this.searchResults[this.searchIndex];
    el.classList.add('search-hit');
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  private clearSearch(): void {
    this.searchResults.forEach(el => el.classList.remove('search-hit'));
    this.searchResults = [];
    this.searchIndex = -1;
  }

  // =============================
  // JSON SEARCH
  // =============================

  private startJsonSearch(): void {
    this.clearJsonSearch();
    if (!this.jsonSearchText || !this.jsonContent) return;

    const needle = this.jsonSearchText.toLowerCase();
    const container = this.jsonContent.nativeElement;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let node: Text | null;

    // ✅ FIRST: collect text nodes (NO mutation)
    while ((node = walker.nextNode() as Text | null)) {
      if (node.nodeValue) {
        nodes.push(node);
      }
    }

    // ✅ SECOND: mutate safely
    nodes.forEach(node => {
      const text = node.nodeValue!;
      const lower = text.toLowerCase();
      if (!lower.includes(needle)) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let index: number;

      while ((index = lower.indexOf(needle, lastIndex)) !== -1) {
        if (index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex, index))
          );
        }

        const span = document.createElement('span');
        span.className = 'json-search-hit';
        span.textContent = text.slice(index, index + needle.length);
        fragment.appendChild(span);
        this.jsonSearchResults.push(span);

        lastIndex = index + needle.length;
      }

      fragment.appendChild(
        document.createTextNode(text.slice(lastIndex))
      );

      node.parentNode!.replaceChild(fragment, node);
    });

    if (this.jsonSearchResults.length) {
      this.jsonSearchIndex = 0;
      this.focusJsonResult();
    }
  }

  private focusJsonResult(): void {
    this.jsonSearchResults.forEach(el =>
      el.classList.remove('json-search-active')
    );

    const el = this.jsonSearchResults[this.jsonSearchIndex];
    el.classList.add('json-search-active');

    this.scrollIntoViewWithPadding(
      el,
      this.jsonContent.nativeElement,
      12
    );
  }

  private clearJsonSearch(): void {
    this.jsonSearchResults.forEach(span => {
      const parent = span.parentNode!;
      parent.replaceChild(document.createTextNode(span.textContent || ''), span);
      parent.normalize();
    });
    this.jsonSearchResults = [];
    this.jsonSearchIndex = -1;
  }

  // =============================
  // XML SEARCH
  // =============================

  private startXmlSearch(): void {
    this.clearXmlSearch();
    if (!this.xmlSearchText || !this.xmlContent) return;

    const needle = this.xmlSearchText.toLowerCase();
    const container = this.xmlContent.nativeElement;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let node: Text | null;

    // 1️⃣ Collect text nodes first
    while ((node = walker.nextNode() as Text | null)) {
      if (node.nodeValue) {
        nodes.push(node);
      }
    }

    // 2️⃣ Mutate after traversal
    nodes.forEach(node => {
      const text = node.nodeValue!;
      const lower = text.toLowerCase();
      if (!lower.includes(needle)) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let index: number;

      while ((index = lower.indexOf(needle, lastIndex)) !== -1) {
        if (index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex, index))
          );
        }

        const span = document.createElement('span');
        span.className = 'json-search-hit';
        span.textContent = text.slice(index, index + needle.length);
        fragment.appendChild(span);
        this.xmlSearchResults.push(span);

        lastIndex = index + needle.length;
      }

      fragment.appendChild(
        document.createTextNode(text.slice(lastIndex))
      );

      node.parentNode!.replaceChild(fragment, node);
    });

    if (this.xmlSearchResults.length) {
      this.xmlSearchIndex = 0;
      this.focusXmlResult();
    }
  }

  private focusXmlResult(): void {
    this.xmlSearchResults.forEach(el =>
      el.classList.remove('json-search-active')
    );

    const el = this.xmlSearchResults[this.xmlSearchIndex];
    el.classList.add('json-search-active');

    this.scrollIntoViewWithPadding(
      el,
      this.xmlContent.nativeElement,
      12
    );
  }

  private scrollIntoViewWithPadding(
    element: HTMLElement,
    container: HTMLElement,
    padding: number
  ): void {
    const elRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    let targetScrollTop = container.scrollTop;

    // Above visible area
    if (elRect.top < containerRect.top + padding) {
      targetScrollTop -= (containerRect.top + padding - elRect.top);
    }

    // Below visible area
    else if (elRect.bottom > containerRect.bottom - padding) {
      targetScrollTop += (elRect.bottom - (containerRect.bottom - padding));
    } else {
      return; // already comfortably visible
    }

    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
  }

  private clearXmlSearch(): void {
    this.xmlSearchResults.forEach(span => {
      const parent = span.parentNode!;
      parent.replaceChild(document.createTextNode(span.textContent || ''), span);
      parent.normalize();
    });
    this.xmlSearchResults = [];
    this.xmlSearchIndex = -1;
  }

  private getMimeType(fileName?: string): string {
    if (!fileName) return 'application/octet-stream';

    const ext = fileName.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'svg': return 'image/svg+xml';
      case 'txt': return 'text/plain';
      case 'html': return 'text/html';
      case 'xml': return 'application/xml';
      case 'json': return 'application/json';
      default: return 'application/octet-stream';
    }
  }

  public formatCellValue(value: any): string {
    // Real null / undefined
    if (value === null || value === undefined) {
      return 'NULL';
    }

    // ISO date string → 12h clock with AM/PM
    if (typeof value === 'string') {
      // Matches: 2026-01-21T14:32:18.123 or 2026-01-21T14:32:18
      const match = value.match(
        /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/
      );

      if (match) {
        const datePart = match[1];
        let hour = Number(match[2]);
        const minute = match[3];

        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12;
        if (hour === 0) hour = 12;

        return `${datePart} ${hour}:${minute} ${ampm}`;
      }

      return value;
    }

    // Primitive values
    if (typeof value === 'number' || typeof value === 'boolean')
      return String(value);

    // Boxed DB NULL patterns (SQL serializers)
    if (typeof value === 'object')
      return 'NULL';

    return String(value);
  }

  // =============================
  // JSON
  // =============================
  public isJson(value: any): boolean {
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();

    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return false;
    }

    try {
      // 1️⃣ Try normal JSON first
      const parsed = JSON.parse(
        trimmed.replace(/,\s*(\}|\])/g, '$1')
      );

      return typeof parsed === 'object' && parsed !== null;
    } catch {
      // 2️⃣ Fallback: multiple top-level objects → wrap as array
      try {
        // Detect pattern: } { (with optional whitespace/newlines)
        const normalized = trimmed.replace(/}\s*{/g, '},{');

        // Must actually change something, otherwise don't lie
        if (normalized === trimmed) return false;

        const wrapped = `[${normalized}]`;
        const parsedArray = JSON.parse(wrapped);

        return Array.isArray(parsedArray) && parsedArray.length > 0;
      } catch {
        return false;
      }
    }
  }

  // =============================
  // RESPONSE COLUMN DETECTION
  // =============================
  public isResponseColumn(col: string): boolean {
    if (!col) return false;

    const normalized = col.toLowerCase();

    return (
      normalized.includes('response') ||
      normalized.endsWith('_response') ||
      normalized.startsWith('response')
    );
  }

  public hasBeforeValue(value: any): boolean {
    if (!this.isJson(value)) return false;

    try {
      const parsed = JSON.parse(
        value.trim().replace(/,\s*(\}|\])/g, '$1')
      );

      // Object case
      if (!Array.isArray(parsed)) {
        return (
          typeof parsed.value === 'string' &&
          parsed.value.startsWith('Before')
        );
      }

      // Array case
      return parsed.some(
        (item: any) =>
          item &&
          typeof item === 'object' &&
          typeof item.value === 'string' &&
          item.value.startsWith('Before')
      );
    } catch {
      return false;
    }
  }

  private highlightJson(json: string): string {
    const escaped = json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      match => {
        if (/^"/.test(match)) {
          return match.endsWith(':')
            ? `<span class="json-key">${match}</span>`
            : `<span class="json-string">${match}</span>`;
        }
        if (/true|false/.test(match)) return `<span class="json-boolean">${match}</span>`;
        if (/null/.test(match)) return `<span class="json-null">${match}</span>`;
        return `<span class="json-number">${match}</span>`;
      }
    );
  }

  private injectContentEyeButtons(container: HTMLElement): void {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Text | null;

    while ((node = walker.nextNode() as Text | null)) {
      if (!node.nodeValue) continue;

      const detected = this.detectBase64Document(node.nodeValue);
      if (detected) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(textNode => {
      const parent = textNode.parentElement;
      if (!parent) return;

      if (parent.querySelector('.json-eye-btn')) return;

      const detection = this.detectBase64Document(textNode.nodeValue!);
      if (!detection) return;

      const btn = document.createElement('button');
      btn.className = 'json-eye-btn no-focus-border';
      btn.title = `Preview ${detection.type}`;
      btn.innerHTML = `
        <span class="eye-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
            <circle cx="12" cy="12" r="2"/>
          </svg>
        </span>
      `;

      btn.onclick = () => {
        this.previewBase64Document(detection.base64, detection.mimeType);
      };

      parent.insertBefore(btn, parent.firstChild);
    });
  }

  private detectBase64Document(text: string): {
    base64: string;
    mimeType: string;
    type: string;
  } | null {
    const cleaned = text.trim().replace(/^"+|"+$/g, '');

    // Minimum size heuristic (PDFs/images are never tiny)
    if (cleaned.length < 100) return null;

    // Base64 character validation
    if (!/^[A-Za-z0-9+/=\s]+$/.test(cleaned)) return null;

    try {
      const binary = atob(cleaned.substring(0, 200));
      const bytes = new Uint8Array(
        [...binary].map(c => c.charCodeAt(0))
      );

      // PDF
      if (this.startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) {
        return { base64: cleaned, mimeType: 'application/pdf', type: 'PDF' };
      }

      // PNG
      if (this.startsWith(bytes, [0x89, 0x50, 0x4E, 0x47])) {
        return { base64: cleaned, mimeType: 'image/png', type: 'PNG' };
      }

      // JPG
      if (this.startsWith(bytes, [0xFF, 0xD8, 0xFF])) {
        return { base64: cleaned, mimeType: 'image/jpeg', type: 'JPG' };
      }

      // GIF
      if (this.startsWith(bytes, [0x47, 0x49, 0x46, 0x38])) {
        return { base64: cleaned, mimeType: 'image/gif', type: 'GIF' };
      }

      return null;
    } catch {
      return null;
    }
  }

  private startsWith(bytes: Uint8Array, signature: number[]): boolean {
    return signature.every((b, i) => bytes[i] === b);
  }

  private previewBase64Document(base64: string, mimeType: string): void {
    const blob = this.base64ToBlob(base64, mimeType);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
  }

  public openJson(value: string): void {
    // Reset renderer state
    this.jsonLines = [];

    // 🔒 Sanitize JSON the SAME way as isJson()
    const trimmed = value.trim();
    const sanitized = trimmed.replace(/,\s*(\}|\])/g, '$1');

    let parsed: any;

    try {
      // 1️⃣ Try normal JSON first
      parsed = JSON.parse(sanitized);
    } catch {
      // 2️⃣ Fallback: multiple top-level objects → wrap as array
      const normalized = sanitized.replace(/}\s*{/g, '},{');

      if (normalized === sanitized) {
        // Should not happen if isJson() was true, but stay safe
        throw new Error('Invalid JSON');
      }

      parsed = JSON.parse(`[${normalized}]`);
    }

    // Keep the exact parsed JSON used by the popup for Copy / Excel export
    this.jsonPopupData = parsed;

    // Render directly from the real object (object OR array)
    this.renderValue(parsed, 0, false);

    // Show popup
    this.jsonPopupVisible = true;
  }

  public openGridJson(): void {
    this.openJson(JSON.stringify(this.rows ?? []));
  }

  /* =============================
     JSON RENDERER
     ============================= */

  private renderValue(value: any, indentLevel: number, comma: boolean): void {
    const indent = ' '.repeat(indentLevel);

    // ---- Primitive (array item) ----
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      this.jsonLines.push({
        kind: 'value',
        indent,
        value: String(value),
        valueType:
          value === null
            ? 'null'
            : typeof value === 'boolean'
              ? 'boolean'
              : typeof value === 'number'
                ? 'number'
                : 'string',
        comma
      });
      return;
    }

    // ---- Array (INLINE, NOT DELEGATED) ----
    if (Array.isArray(value)) {
      this.jsonLines.push({ kind: 'brace', indent, text: '[' });

      value.forEach((item, i) => {
        this.renderValue(item, indentLevel + 1, i < value.length - 1);
      });

      this.jsonLines.push({
        kind: 'brace',
        indent,
        text: ']',
        comma
      });
      return;
    }

    // ---- Object ----
    this.jsonLines.push({ kind: 'brace', indent, text: '{' });

    const entries = Object.entries(value);
    entries.forEach(([key, val], i) => {
      const isLast = i === entries.length - 1;
      const childIndent = ' '.repeat(indentLevel + 1);

      // ---- Shopify Variant image preview ----
      if (key.toLowerCase() === 'pro_shopifyid' && val !== null && val !== undefined) {
        this.jsonLines.push({
          kind: 'content',
          indent: childIndent,
          key,
          value: String(val),
          comma: !isLast
        });
        return;
      }

      // ---- Content special case (template-compatible) ----
      if (typeof val === 'string') {
        const doc = this.detectBase64Document(val);

        if (doc) {
          this.jsonLines.push({
            kind: 'content',
            indent: childIndent,
            key,
            value: val,
            base64: doc.base64,
            preview: `${doc.type} document`,
            mimeType: doc.mimeType,
            comma: !isLast
          });
          return;
        }
      }

      // ---- Array property (INLINE) ----
      if (Array.isArray(val)) {
        this.jsonLines.push({
          kind: 'pair',
          indent: childIndent,
          key,
          comma: false
        });

        this.renderValue(val, indentLevel + 1, !isLast);
        return;
      }

      // ---- Object property ----
      if (typeof val === 'object' && val !== null) {
        const isEmptyObject = !Array.isArray(val) && Object.keys(val).length === 0;

        if (isEmptyObject) {
          this.jsonLines.push({
            kind: 'pair',
            indent: childIndent,
            key,
            value: 'null',
            valueType: 'null',
            comma: !isLast
          });
          return;
        }

        this.jsonLines.push({
          kind: 'pair',
          indent: childIndent,
          key,
          comma: false
        });

        this.renderValue(val, indentLevel + 1, !isLast);
        return;
      }

      // ---- Primitive property ----
      this.jsonLines.push({
        kind: 'pair',
        indent: childIndent,
        key,
        value: String(val),
        valueType:
          val === null
            ? 'null'
            : typeof val === 'boolean'
              ? 'boolean'
              : typeof val === 'number'
                ? 'number'
                : 'string',
        comma: !isLast
      });
    });

    this.jsonLines.push({
      kind: 'brace',
      indent,
      text: '}',
      comma
    });
  }

  private getJsonCssClass(line: string): string {
    if (line.trim().startsWith('"')) return 'json-key';
    if (line.includes('"')) return 'json-string';
    if (line.includes('true') || line.includes('false')) return 'json-boolean';
    if (line.includes('null')) return 'json-null';
    if (!isNaN(Number(line.trim()))) return 'json-number';
    return '';
  }

  public async copyEntireJson(): Promise<void> {
    if (this.jsonPopupData === null || this.jsonPopupData === undefined) return;

    const json = JSON.stringify(this.jsonPopupData, null, 2);

    try {
      await navigator.clipboard.writeText(json);
      this.playCopySound();
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = json;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      this.playCopySound();
    }
  }

  public async exportJsonToExcel(): Promise<void> {
    if (this.jsonPopupData === null || this.jsonPopupData === undefined) return;

    const rows = this.normalizeJsonForExcel(this.jsonPopupData);
    if (!rows.length) {
      await Swal.fire('', 'There is no data to export.', 'info');
      return;
    }

    const result = await Swal.fire({
      title: 'Export to Excel',
      input: 'text',
      inputLabel: 'File name',
      inputValue: `SQL-Result-${this.getExportTimestamp()}.xlsx`,
      showCancelButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      inputValidator: value => {
        const fileName = String(value ?? '').trim();
        if (!fileName) return 'Please enter a file name.';
        return null;
      }
    });

    if (!result.isConfirmed) return;

    let fileName = String(result.value ?? '').trim();
    if (!fileName.toLowerCase().endsWith('.xlsx')) fileName += '.xlsx';

    const workbook = this.buildXlsxWorkbook(rows);

    // BlobPart expects an ArrayBuffer in Angular/TypeScript's current DOM typings.
    // Copy the generated Uint8Array into a guaranteed ArrayBuffer to avoid
    // Uint8Array<ArrayBufferLike> / SharedArrayBuffer type incompatibility.
    const workbookBuffer = new ArrayBuffer(workbook.byteLength);
    new Uint8Array(workbookBuffer).set(workbook);

    const blob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    try {
      const picker = (window as any).showSaveFilePicker;

      if (typeof picker === 'function') {
        const handle = await picker({
          suggestedName: fileName,
          types: [
            {
              description: 'Excel Workbook',
              accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
              }
            }
          ]
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      }

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error(error);
      await Swal.fire('Error', 'Unable to export the Excel file.', 'error');
    }
  }

  private normalizeJsonForExcel(data: any): Record<string, any>[] {
    const source = Array.isArray(data) ? data : [data];

    if (!source.length) return [];

    if (source.every(item => item !== null && typeof item === 'object' && !Array.isArray(item))) {
      return source.map(item => ({ ...item }));
    }

    return source.map(item => ({ Value: item }));
  }

  private buildXlsxWorkbook(rows: Record<string, any>[]): Uint8Array {
    const columns: string[] = [];
    const seen = new Set<string>();

    for (const row of rows) {
      for (const key of Object.keys(row ?? {})) {
        if (!seen.has(key)) {
          seen.add(key);
          columns.push(key);
        }
      }
    }

    if (!columns.length) columns.push('Value');

    const displayRows = rows.map(row =>
      columns.map(column => this.toExcelCellValue(row?.[column]))
    );

    const widths = columns.map((column, columnIndex) => {
      const headerLength = this.getExcelDisplayLength(column);
      let maxLength = headerLength;

      for (const row of displayRows) {
        maxLength = Math.max(maxLength, this.getExcelDisplayLength(row[columnIndex]));
      }

      // Excel column width is not an exact character count. Give the longest
      // value extra room and reserve additional space for the AutoFilter arrow
      // so both headers and cell values are fully visible when the file opens.
      const contentWidth = maxLength + 4;
      const headerWidth = headerLength + 5;

      return Math.min(255, Math.max(8, contentWidth, headerWidth));
    });

    const lastColumn = this.getExcelColumnName(columns.length);
    const lastRow = displayRows.length + 1;
    const range = `A1:${lastColumn}${lastRow}`;

    const colsXml = widths
      .map((width, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${width}" bestFit="1" customWidth="1"/>`
      )
      .join('');

    const headerCells = columns
      .map((column, index) => this.createXlsxCell(1, index + 1, column, 1))
      .join('');

    const dataRowsXml = displayRows
      .map((row, rowIndex) => {
        const excelRow = rowIndex + 2;
        const styleIndex = rowIndex % 2 === 0 ? 2 : 0;
        const cells = row
          .map((value, columnIndex) => this.createXlsxCell(excelRow, columnIndex + 1, value, styleIndex))
          .join('');
        return `<row r="${excelRow}">${cells}</row>`;
      })
      .join('');

    const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <cols>${colsXml}</cols>
  <sheetData>
    <row r="1">${headerCells}</row>
    ${dataRowsXml}
  </sheetData>
  <autoFilter ref="${range}"/>
</worksheet>`;

    const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF4EA72E"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFD9EFD3"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FF70AD47"/></left>
      <right style="thin"><color rgb="FF70AD47"/></right>
      <top style="thin"><color rgb="FF70AD47"/></top>
      <bottom style="thin"><color rgb="FF70AD47"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="3">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"><alignment vertical="center"/></xf>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1"><alignment vertical="center"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

    const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="SQL Result" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

    const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

    const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

    const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

    return this.createStoredZip([
      { name: '[Content_Types].xml', content: contentTypesXml },
      { name: '_rels/.rels', content: rootRelsXml },
      { name: 'xl/workbook.xml', content: workbookXml },
      { name: 'xl/_rels/workbook.xml.rels', content: workbookRelsXml },
      { name: 'xl/worksheets/sheet1.xml', content: sheetXml },
      { name: 'xl/styles.xml', content: stylesXml }
    ]);
  }

  private createXlsxCell(row: number, column: number, value: any, styleIndex: number): string {
    const ref = `${this.getExcelColumnName(column)}${row}`;

    if (value === null || value === undefined || value === '') {
      return `<c r="${ref}" s="${styleIndex}" t="inlineStr"><is><t></t></is></c>`;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return `<c r="${ref}" s="${styleIndex}"><v>${value}</v></c>`;
    }

    if (typeof value === 'boolean') {
      return `<c r="${ref}" s="${styleIndex}" t="b"><v>${value ? 1 : 0}</v></c>`;
    }

    const text = this.escapeXml(String(value));
    return `<c r="${ref}" s="${styleIndex}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
  }

  private toExcelCellValue(value: any): any {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  }

  private getExcelDisplayLength(value: any): number {
    const text = String(value ?? '');
    return text.split(/\r?\n/).reduce((max, line) => Math.max(max, line.length), 0);
  }

  private getExcelColumnName(column: number): string {
    let result = '';
    let n = column;

    while (n > 0) {
      const remainder = (n - 1) % 26;
      result = String.fromCharCode(65 + remainder) + result;
      n = Math.floor((n - 1) / 26);
    }

    return result;
  }

  private escapeXml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private getExportTimestamp(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  }

  private createStoredZip(files: { name: string; content: string }[]): Uint8Array {
    const encoder = new TextEncoder();
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    const now = new Date();
    const dosTime = ((now.getHours() & 0x1f) << 11) | ((now.getMinutes() & 0x3f) << 5) | ((Math.floor(now.getSeconds() / 2)) & 0x1f);
    const dosDate = (((now.getFullYear() - 1980) & 0x7f) << 9) | (((now.getMonth() + 1) & 0x0f) << 5) | (now.getDate() & 0x1f);

    for (const file of files) {
      const nameBytes = encoder.encode(file.name);
      const data = encoder.encode(file.content);
      const crc = this.crc32(data);

      const localHeader = new Uint8Array(30 + nameBytes.length);
      const localView = new DataView(localHeader.buffer);
      localView.setUint32(0, 0x04034b50, true);
      localView.setUint16(4, 20, true);
      localView.setUint16(6, 0, true);
      localView.setUint16(8, 0, true);
      localView.setUint16(10, dosTime, true);
      localView.setUint16(12, dosDate, true);
      localView.setUint32(14, crc, true);
      localView.setUint32(18, data.length, true);
      localView.setUint32(22, data.length, true);
      localView.setUint16(26, nameBytes.length, true);
      localView.setUint16(28, 0, true);
      localHeader.set(nameBytes, 30);

      localParts.push(localHeader, data);

      const centralHeader = new Uint8Array(46 + nameBytes.length);
      const centralView = new DataView(centralHeader.buffer);
      centralView.setUint32(0, 0x02014b50, true);
      centralView.setUint16(4, 20, true);
      centralView.setUint16(6, 20, true);
      centralView.setUint16(8, 0, true);
      centralView.setUint16(10, 0, true);
      centralView.setUint16(12, dosTime, true);
      centralView.setUint16(14, dosDate, true);
      centralView.setUint32(16, crc, true);
      centralView.setUint32(20, data.length, true);
      centralView.setUint32(24, data.length, true);
      centralView.setUint16(28, nameBytes.length, true);
      centralView.setUint16(30, 0, true);
      centralView.setUint16(32, 0, true);
      centralView.setUint16(34, 0, true);
      centralView.setUint16(36, 0, true);
      centralView.setUint32(38, 0, true);
      centralView.setUint32(42, offset, true);
      centralHeader.set(nameBytes, 46);
      centralParts.push(centralHeader);

      offset += localHeader.length + data.length;
    }

    const centralOffset = offset;
    const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);

    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    endView.setUint32(0, 0x06054b50, true);
    endView.setUint16(4, 0, true);
    endView.setUint16(6, 0, true);
    endView.setUint16(8, files.length, true);
    endView.setUint16(10, files.length, true);
    endView.setUint32(12, centralSize, true);
    endView.setUint32(16, centralOffset, true);
    endView.setUint16(20, 0, true);

    const totalLength = localParts.reduce((sum, part) => sum + part.length, 0) + centralSize + end.length;
    const output = new Uint8Array(totalLength);
    let position = 0;

    for (const part of localParts) {
      output.set(part, position);
      position += part.length;
    }

    for (const part of centralParts) {
      output.set(part, position);
      position += part.length;
    }

    output.set(end, position);
    return output;
  }

  private crc32(data: Uint8Array): number {
    let crc = 0xffffffff;

    for (let i = 0; i < data.length; i++) {
      crc ^= data[i];
      for (let bit = 0; bit < 8; bit++) {
        crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
      }
    }

    return (crc ^ 0xffffffff) >>> 0;
  }

  public closeJson(): void {
    this.jsonPopupVisible = false;
    this.jsonPopupData = null;
  }

  // =============================
  // XML
  // =============================

  public isXml(value: any): boolean {
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();
    if (!trimmed.startsWith('<')) return false;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(trimmed, 'application/xml');
      return !doc.querySelector('parsererror');
    } catch {
      return false;
    }
  }

  private highlightXml(xml: string): string {
    const escaped = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped
      .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="xml-comment">$1</span>')
      .replace(
        /(&lt;\/?)([\w:-]+)([^&]*?)(\/?&gt;)/g,
        (_, p1, tag, attrs, p4) =>
          `${p1}<span class="xml-tag">${tag}</span>${attrs.replace(
            /([\w:-]+)=(".*?")/g,
            '<span class="xml-attr">$1</span>=<span class="xml-value">$2</span>'
          )}${p4}`
      );
  }

  public openRowJson(row: any): void {
    this.openJson(JSON.stringify(row ?? {}));
  }
  public openXml(value: string): void {
    const parser = new DOMParser();
    const xml = parser.parseFromString(value, 'application/xml');

    const serializer = new XMLSerializer();
    const raw = serializer.serializeToString(xml);
    const formatted = this.formatXml(raw);
    const highlighted = this.highlightXml(formatted);

    this.xmlPopupContent = this.sanitizer.bypassSecurityTrustHtml(highlighted);
    this.xmlPopupVisible = true;
    this.clearXmlSearch();
    this.xmlSearchText = '';
  }

  private formatXml(xml: string): string {
    const lines = xml.replace(/>\s*</g, '>\n<').split('\n');
    let indent = 0;
    const indentSize = 2;

    return lines
      .map(line => {
        line = line.trim();

        const isClosingTag = /^<\/.+>$/.test(line);
        const isSelfClosingTag = /^<.+\/>$/.test(line);
        const isFullTag = /^<[^>]+>.*<\/[^>]+>$/.test(line);
        const isOpeningTag = /^<[^!?/][^>]*>$/.test(line);

        if (isClosingTag) {
          indent = Math.max(indent - 1, 0);
        }

        const padded = ' '.repeat(indent * indentSize) + line;

        if (isOpeningTag && !isFullTag && !isSelfClosingTag) {
          indent++;
        }

        return padded;
      })
      .join('\n');
  }

  public closeXml(): void {
    this.xmlPopupVisible = false;
  }

  // =============================
  // HTML
  // =============================

  public isHtml(value: any): boolean {
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();
    if (!trimmed.startsWith('<')) return false;
    if (this.isXml(trimmed)) return false;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(trimmed, 'text/html');
      return !!doc.body && doc.body.children.length > 0;
    } catch {
      return false;
    }
  }

  public openHtml(value: string): void {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    const decoded = textarea.value.replace(/^\s+/, '');

    const highlighted = decoded
      .replace(/\b([A-Z][A-Za-z0-9_]+Exception)\b/g, '<span class="cs-exception">$1</span>')
      .replace(/\bat\b/g, '<span class="cs-keyword">at</span>')
      .replace(
        /\b([A-Za-z_][A-Za-z0-9_.]+)\.([A-Za-z_][A-Za-z0-9_<>]+)\b/g,
        '<span class="cs-namespace">$1</span>.<span class="cs-method">$2</span>'
      )
      .replace(/([A-Z]:\\[^:<>\n]+\.cs)/g, '<span class="cs-path">$1</span>')
      .replace(/line\s+(\d+)/gi, 'line <span class="cs-line">$1</span>');

    this.htmlPopupContent = this.sanitizer.bypassSecurityTrustHtml(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body {
            background: #1e1e1e;
            color: #d4d4d4;
            font-family: Consolas, monospace;
            margin: 0;
            padding: 12px;
            white-space: pre-wrap;
          }

          /* ===== STACKTRACE / HTML SYNTAX COLORS ===== */
          .cs-exception {
            color: #f48771;
            font-weight: bold;
          }

          .cs-keyword {
            color: #569cd6;
            font-weight: bold;
          }

          .cs-namespace {
            color: #4ec9b0;
          }

          .cs-method {
            color: #dcdcaa;
          }

          .cs-path {
            color: #ce9178;
          }

          .cs-line {
            color: #b5cea8;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
      ${highlighted}
      </body>
      </html>
  `);

    this.htmlPopupVisible = true;
  }

  public closeHtml(): void {
    this.htmlPopupVisible = false;
    this.htmlPopupContent = '';
  }

  // =============================
  // KEYBOARD
  // =============================

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const isSqlEditor =
      !!target.closest('.cm-editor') ||
      target === this.sqlEditorHost?.nativeElement;

    if (event.key === 'Enter' && isSqlEditor) return;
    if (this.resizingIndex !== null) return;

    if (this.shoKeyPopoverVisible) {
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closeShoKeyPopover();
      }
      return;
    }

    if (this.jsonPopupVisible && !this.shopifyPreviewVisible) {
      if (event.key === 'Enter') {
        this.swallow(event);
        return this.startJsonSearch();
      }
      if (event.key === 'F3' && this.jsonSearchResults.length) {
        this.swallow(event);
        this.jsonSearchIndex = (this.jsonSearchIndex + 1) % this.jsonSearchResults.length;
        return this.focusJsonResult();
      }
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closeJson();
      }
      return;
    }

    if (this.xmlPopupVisible) {
      if (event.key === 'Enter') {
        this.swallow(event);
        return this.startXmlSearch();
      }
      if (event.key === 'F3' && this.xmlSearchResults.length) {
        this.swallow(event);
        this.xmlSearchIndex = (this.xmlSearchIndex + 1) % this.xmlSearchResults.length;
        return this.focusXmlResult();
      }
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closeXml();
      }
      return;
    }

    if (this.htmlPopupVisible) {
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closeHtml();
      }
      return;
    }

    if (this.pricePopupVisible) {
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closePricePopup();
      }
      return;
    }
    
    if (this.shopifyPreviewVisible) {
      if (event.key === 'Escape') {
        this.swallow(event);
        return this.closeShopifyPreview();
      }
      return;
    }

    if (event.key === 'Enter') {
      this.swallow(event);
      return this.startSearch();
    }

    if (event.key === 'F3' && this.gridSearchResults.length) {
      this.swallow(event);
      this.gridSearchIndex =
        (this.gridSearchIndex + 1) % this.gridSearchResults.length;
      this.focusGridResult();
    }

    if (event.key === 'F5') {
      this.swallow(event);

      if (this.cmEditor) {
        const sel = this.cmEditor.state.selection.main;
        const selected = sel.empty
          ? ''
          : this.cmEditor.state.doc.sliceString(sel.from, sel.to).trim();

        const fullSql = this.cmEditor.state.doc.toString();
        this.executeSql(selected || fullSql);
        return;
      }

      const editor = this.sqlEditor?.nativeElement;

      if (!editor) {
        this.executeSql();
        return;
      }

      const { selectionStart, selectionEnd, value } = editor;
      const selected = value.substring(selectionStart, selectionEnd).trim();
      this.executeSql(selected || value);
    }
  }

  private swallow(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }
}
